import { useMemo, useState } from 'react'
import compact from 'lodash/compact'
import toString from 'lodash/toString'
import toInteger from 'lodash/toInteger'
import { useOrders } from '@klear/ui/hooks/use-orders'
import type { ComboboxOption } from '@klear/ui/components/combobox'
import { formatSummaryOrderValue } from '@klear/order/list-display'
import { EntitySelectOrCreate } from '@/app/workflow/EntitySelectOrCreate'

export type OrderSelectOrCreateProps = {
  traderId: number
  defaultOrderId?: number | null
  onSelected: (orderId: number) => void
  onCreateNew: () => void
}

export function OrderSelectOrCreate({ traderId, defaultOrderId, onSelected, onCreateNew }: OrderSelectOrCreateProps) {
  const { orders, isLoading } = useOrders(traderId || undefined, { isArchived: false })
  const [orderId, setOrderId] = useState<number | null>(defaultOrderId ?? null)

  const orderOptions = useMemo<ComboboxOption[]>(
    () =>
      orders.map((order) => {
        const idLabel = order.idDisplay ? `#${order.idDisplay}` : `Order ${order.id}`
        const customerName = order.customerOrganizationName ?? null
        const valueText = order.listSummary ? formatSummaryOrderValue(order.listSummary) : null
        const leftText = compact([idLabel, customerName]).join(' · ')
        const label = (
          <span className="flex w-full items-center justify-between gap-4">
            <span>{leftText}</span>
            {valueText && <span className="tabular-nums text-muted-foreground ml-auto">{valueText}</span>}
          </span>
        )
        return {
          value: toString(order.id),
          label,
          keywords: compact([idLabel, customerName, valueText])
        }
      }),
    [orders]
  )

  return (
    <EntitySelectOrCreate
      inlineCreate
      isLoading={isLoading}
      selectTitle="Select order"
      selectDescription="Pick the order to attach, or create a new one."
      selectFieldLabel="Order"
      options={orderOptions}
      selectedValue={orderId != null ? toString(orderId) : ''}
      onSelectedChange={(v) => setOrderId(v ? toInteger(v) : null)}
      onSelectConfirm={() => orderId != null && onSelected(orderId)}
      selectConfirmDisabled={orderId == null}
      selectTriggerLabel="Select order…"
      selectPlaceholder="Search orders…"
      createTitle="Create a new order"
      createDescription="Start a new order for a customer, then build its purchase order in the next step."
      createActionLabel="Create"
      onCreateNew={onCreateNew}
    />
  )
}
